/**
 * PrintLabelButton — tiny inline button that streams a one-row PDF label
 * from /api/qr-labels/render and triggers a browser download.
 *
 * Used in the POS Products list and Assets list rows.
 *
 * Props:
 *   entity:    'product' | 'asset'
 *   id:        the row id
 *   name?:     optional display name (used in toast / filename)
 *   layout?:   defaults to 'avery_5160' for products and 'dymo_30252' for assets
 *   className: pass-through
 *
 * Behaviour: single click → instant 1-label PDF download.
 * Shift-click → opens a small layout/copies prompt for customisation.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Printer, RefreshCw, Plus } from 'lucide-react';
import { getToken } from '@/lib/authToken';
import { useLabelBatch } from '@/lib/useLabelBatch';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const DEFAULT_LAYOUT = {
  product: 'avery_5160',
  asset: 'dymo_30252',
};

export const renderLabelsPdf = async ({ entity, ids, layout, copies = 1,
                                          includeName = true, includeCode = true }) => {
  const res = await axios.post(
    `${API}/api/qr-labels/render`,
    {
      entity, ids, layout,
      copies_per_item: copies,
      include_name: includeName,
      include_code: includeCode,
    },
    { headers: authH(), responseType: 'blob' }
  );
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `qr-labels-${entity}-${layout}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const PrintLabelButton = ({ entity, id, name = '', className = '', size = 'icon',
                              testId, variant = 'ghost', showBatch = true }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT[entity] || 'avery_5160');
  const [copies, setCopies] = useState(1);
  const addToBatch = useLabelBatch(s => s.add);

  const print = async (opts = {}) => {
    setBusy(true);
    try {
      await renderLabelsPdf({
        entity, ids: [id],
        layout: opts.layout || layout,
        copies: opts.copies || copies,
      });
      toast.success(`Label PDF downloaded${name ? ` · ${name}` : ''}`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Print failed');
    } finally {
      setBusy(false);
    }
  };

  const onClick = (e) => {
    if (e.shiftKey || e.altKey) {
      setOpen(true);
    } else {
      print({ layout: DEFAULT_LAYOUT[entity], copies: 1 });
    }
  };

  return (
    <>
      <div className={`inline-flex items-center gap-0.5 ${className}`}>
        <Button
          variant={variant}
          size={size}
          className="h-7 w-7"
          onClick={onClick}
          disabled={busy}
          data-testid={testId || `print-label-${entity}-${id}`}
          title="Print QR label (shift-click for options)"
        >
          {busy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Printer className="h-3.5 w-3.5" />}
        </Button>
        {showBatch && (
          <Button
            variant={variant}
            size={size}
            className="h-7 w-5"
            onClick={() => {
              addToBatch({ entity, id, name });
              toast.success('Added to label batch');
            }}
            title="Add to label batch"
            data-testid={`add-to-batch-${entity}-${id}`}
          >
            <Plus className="h-3 w-3" />
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader>
            <DialogTitle>Print QR label{name ? ` — ${name}` : ''}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Choose a label layout and how many copies. PDF downloads instantly when you confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Layout</label>
              <Select value={layout} onValueChange={setLayout}>
                <SelectTrigger className="bg-slate-900 border-slate-800" data-testid="print-label-layout">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="avery_5160">Avery 5160 (sheet, 30/page)</SelectItem>
                  <SelectItem value="avery_5163">Avery 5163 (sheet, 10/page)</SelectItem>
                  <SelectItem value="brother_dk_22205">Brother DK-22205 (continuous 62mm)</SelectItem>
                  <SelectItem value="dymo_30252">Dymo 30252 (address 89×28mm)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Copies</label>
              <Input type="number" min={1} max={100} value={copies}
                     onChange={e => setCopies(Math.max(1, Math.min(100, +e.target.value || 1)))}
                     className="bg-slate-900 border-slate-800"
                     data-testid="print-label-copies" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => print()} disabled={busy} data-testid="print-label-confirm">
              {busy ? 'Generating…' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PrintLabelButton;
